(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.KQBrushBundleCore = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var MARKER = '_kq_brush_swap';

  function properties(line) {
    var props = Object.assign({}, line.properties || {});
    delete props[MARKER];
    return props;
  }

  // Shopify cannot remove the last line-item property: `properties` on
  // /cart/change.js replaces the whole object, and an empty object leaves the
  // key in place. So a completed swap parks the marker on an empty value and
  // we treat that as "no marker" from here on. This costs one extra read per
  // line but avoids a /cart/change.js round trip per brush line.
  function markerOf(line) {
    var marker = line.properties && line.properties[MARKER];
    return marker ? marker : null;
  }

  function planId(line) {
    return line.selling_plan_allocation && line.selling_plan_allocation.selling_plan.id || null;
  }

  function identity(id, props, sellingPlan) {
    return JSON.stringify([String(id), String(sellingPlan || ''), Object.keys(props).sort().map(function (key) {
      return [key, props[key]];
    })]);
  }

  function isBrush(line, config) {
    return String(line.product_id) === String(config.productId);
  }

  function sourceFor(cart, stage, source) {
    var expected = identity(source.id, source.properties, source.sellingPlan);
    var matches = cart.items.filter(function (line) {
      return line !== stage && identity(line.variant_id, properties(line), planId(line)) === expected;
    });
    var keyed = matches.find(function (line) { return line.key === source.key; });
    if (keyed) return keyed;
    // Discount applications can change a key. Only use an unambiguous identity.
    if (matches.length > 1) throw new Error('Brush source line is ambiguous');
    return matches[0];
  }

  function plan(cart, config) {
    var brushes = cart.items.filter(function (line) { return isBrush(line, config); });
    if (!brushes.length) return null;
    if (!config.standardId || !config.bundleId || config.standardId === config.bundleId) {
      throw new Error('Brush variants are not configured');
    }
    var hasBed = cart.items.some(function (line) {
      return line.quantity > 0 && config.bedProductIds.some(function (id) {
        return String(id) === String(line.product_id);
      });
    });
    var target = hasBed ? config.bundleId : config.standardId;
    var stage = brushes.find(function (line) { return markerOf(line); });
    if (stage) {
      var source = JSON.parse(markerOf(stage));
      var original = sourceFor(cart, stage, source);
      if (original) {
        var invalid = stage.quantity !== source.quantity || original.quantity !== source.quantity;
        if (invalid || String(stage.variant_id) !== String(target)) {
          return { type: 'rollback', body: { id: stage.key, quantity: 0 }, error: invalid };
        }
        return { type: 'remove', stage: stage, source: source, body: { id: original.key, quantity: 0 } };
      }
      var props = properties(stage);
      // Shopify cannot clear the last property with {}. A blank private value
      // ends the transaction without adding visible cart/checkout metadata.
      props[MARKER] = '';
      return { type: 'finalize', body: { id: stage.key, quantity: stage.quantity, properties: props } };
    }
    var line = brushes.find(function (item) { return String(item.variant_id) !== String(target); });
    if (!line) return null;
    if (![String(config.standardId), String(config.bundleId)].includes(String(line.variant_id))) {
      throw new Error('Unexpected brush variant');
    }
    var source = { key: line.key, id: line.variant_id, quantity: line.quantity, properties: properties(line), sellingPlan: planId(line) };
    var item = { id: target, quantity: line.quantity, properties: Object.assign({}, source.properties) };
    item.properties[MARKER] = JSON.stringify(source);
    if (source.sellingPlan) item.selling_plan = source.sellingPlan;
    // Add first: an unavailable replacement must never delete the paid item.
    return { type: 'add', source: source, marker: item.properties[MARKER], body: { items: [item] } };
  }

  function checkCancelled(signal) {
    if (!signal || !signal.aborted) return;
    var error = new Error('Cart reconciliation cancelled');
    error.name = 'AbortError';
    throw error;
  }

  async function reconcile(options) {
    var cart = options.cart || await options.getCart();
    var limit = cart.items.length * 4 + 4;
    for (var i = 0; i < limit; i++) {
      checkCancelled(options.signal);
      var step = plan(cart, options.config);
      if (!step) return cart;
      if (options.changed) options.changed();
      try {
        var result = await options.post(step.type === 'add' ? 'cart/add.js' : 'cart/change.js', step.body);
        checkCancelled(options.signal);
        cart = step.type === 'add' ? await options.getCart() : result;
      } catch (error) {
        if ((!options.signal || !options.signal.aborted) && (step.type === 'add' || step.type === 'remove')) {
          // A 422 can partially add stock; a lost response can also hide a
          // successful write. Roll back only if the original still exists.
          try {
            var latest = await options.getCart();
            var staged = latest.items.find(function (line) {
              return isBrush(line, options.config) && line.properties &&
                line.properties[MARKER] === (step.marker || step.stage.properties[MARKER]);
            });
            if (staged && sourceFor(latest, staged, step.source)) {
              checkCancelled(options.signal);
              await options.post('cart/change.js', { id: staged.key, quantity: 0 });
            }
          } catch (_rollbackError) { /* the next run recovers the marked stage */ }
        }
        throw error;
      }
      if (step.error) throw new Error('Brush replacement quantity changed');
    }
    throw new Error('Brush reconciliation did not converge');
  }

  return { MARKER: MARKER, plan: plan, reconcile: reconcile };
});
